import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { playLaunchSound, vibrateLaunch } from "@/lib/launchFeedback";

type Tone = {
  shell: string;
  label: string;
};

const TONES: Record<string, Tone> = {
  ki: {
    shell:
      "border-[#d8e7f2] bg-white/85 text-[#3c5468] hover:border-[#2f8cbf] hover:text-[#2f8cbf]",
    label: "border-[#d8e7f2] bg-white/95 text-[#1a5680]",
  },
  karriere: {
    shell:
      "border-[#e0e6d5] bg-[#fbfaf3]/90 text-[#4b5c42] hover:border-[#6f8a56] hover:text-[#3f5535]",
    label: "border-[#e0e6d5] bg-[#fbfaf3] text-[#3f5535]",
  },
  fische: {
    shell:
      "border-[#ebe0d2] bg-white/90 text-[#6e5340] hover:border-[#b36b1e] hover:text-[#8e5314]",
    label: "border-[#ebe0d2] bg-white text-[#8e5314]",
  },
  default: {
    shell:
      "border-line bg-bg/80 text-ink hover:border-sage hover:text-sage",
    label: "border-line bg-bg/90 text-ink",
  },
};

function toneForPath(pathname: string): Tone {
  if (pathname.startsWith("/ki")) return TONES.ki;
  if (pathname.startsWith("/karriere")) return TONES.karriere;
  if (pathname.startsWith("/projekte") || pathname.startsWith("/fische"))
    return TONES.fische;
  return TONES.default;
}

/**
 * Rocket button, sitting directly next to the settings gear in the bottom right.
 * Same diameter, border and line-icon language as the gear. Clicking launches
 * the icon and scrolls back to the top.
 */
export default function RocketToTop() {
  const { pathname } = useLocation();
  const [visible, setVisible] = useState(false);
  const [firing, setFiring] = useState(false);
  const resetTimer = useRef<number | null>(null);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 260);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    return () => {
      if (resetTimer.current) window.clearTimeout(resetTimer.current);
    };
  }, []);

  const tone = toneForPath(pathname);

  const fire = () => {
    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    setFiring(false);
    window.requestAnimationFrame(() => setFiring(true));

    playLaunchSound();
    vibrateLaunch();

    if (resetTimer.current) window.clearTimeout(resetTimer.current);
    resetTimer.current = window.setTimeout(() => setFiring(false), 1560);

    window.setTimeout(
      () => {
        window.scrollTo({ top: 0, behavior: reduceMotion ? "auto" : "smooth" });
      },
      reduceMotion ? 0 : 170
    );
  };

  return (
    <button
      type="button"
      onClick={fire}
      aria-label="Mit der Rakete zurück nach oben"
      title="Mit der Rakete nach oben"
      className={`rocket-btn group fixed bottom-6 right-20 z-[70] flex h-12 w-12 min-h-[3rem] min-w-[3rem] aspect-square shrink-0 cursor-pointer items-center justify-center rounded-full border backdrop-blur-md transition-all duration-300 ${tone.shell} ${
        visible
          ? "scale-100 opacity-100"
          : "pointer-events-none scale-90 opacity-0"
      } ${firing ? "is-firing" : ""}`}
    >
      <span
        className={`pointer-events-none absolute right-full mr-3 whitespace-nowrap rounded-md border px-2.5 py-1 text-[10px] uppercase tracking-[0.2em] opacity-0 transition-opacity duration-200 group-hover:opacity-100 ${tone.label}`}
      >
        Nach oben
      </span>

      <i className="rk-icon ri-rocket-line text-lg" aria-hidden="true"></i>
    </button>
  );
}